import { useAsyncEffect, useDebounce } from "ahooks";
import { isArray, isEmpty, isEqual, isNil, lowerCase, omit } from "lodash";
import React, { useRef } from "react";
import { useMutation } from "react-query";
import {
    Guest,
    LineItem,
    LineItemCode,
    LineItemType,
    PaymentMethod,
    Purchase,
    PurchaseItemObject,
    PurchaseRuleFilterError,
    PurchaseStatus,
    RewardCodeObject,
} from "types";
import {
    areGuestsEqual,
    captureAddOnRestrictionErrors,
    excludeCatalogOwnedLineItems,
    getAddonLineItemsPayload,
    getFilteredWareLineItems,
    getGuestsPayload,
    getPurchaseLineItems,
    isGuestNotFoundError,
    isPurchaseNotFoundError,
    resetPurchase,
} from "./helpers/PurchaseCreateHelpers";
import { savePurchase } from "./hooks/useUpsertPurchase";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";
import { useStripeTerminal } from "@/hooks/useStripeTerminal";
import { useStripeTerminalDisplay } from "@/hooks/useStripeTerminalDisplay";
import { isTaxFee } from "@/models/LineItem";
import { isPackagePurchase } from "@/models/Purchase";
import { getCouponLineItems, getMembershipCodeLineItems, isScheduledItem } from "@/models/PurchaseItem";
import { CreatePurchaseItemLineItem, CreatePurchaseState, useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";

export const PurchaseManager = React.memo(() => {
    usePurchaseManager();

    // Initialize Stripe terminal and display hook for all purchase screens
    // This ensures cart info is displayed throughout the entire purchase flow (MIST and single-item)
    const readerTerminal = useStripeTerminal();
    useStripeTerminalDisplay(readerTerminal);

    return null;
});

/*
  This component handles purchase create/updates via an API call based on changes to any of the following dependencies:
  finalPurchase, rerunTrigger, showCouponRestriction, or ignoreRestrictions.

  If no purchaseId is present, it uses the immediate purchase instead of a debounced one for faster API updates.
  It performs guard condition checks directly from the store using getState for better performance, avoiding unnecessary reactivity.

  While a request is in flight, any new purchase updates are stored in the pendingUpdate ref.
  Once the in-flight request completes, if there's a pending update, rerunTrigger is used to trigger the next API call.

  The API call is executed through the useUpdatePurchase mutation, and its async behavior is managed by the useAsyncEffect hook.
  The inFlight ref prevents overlapping requests by tracking ongoing API calls.
  The lastCommitted ref keeps a snapshot of the last successfully submitted purchase, enabling accurate change detection.
*/
export const usePurchaseManager = () => {
    const [purchase, showCouponRestriction, rerunTrigger, ignoreRestrictions] = useCreatePurchaseStore((state) => [
        state.purchase,
        state.shouldShowCodeRestrictions.isOpen,
        state.rerunTrigger,
        state.shouldIgnoreRestrictions,
    ]);

    const { mutateAsync: updatePurchase } = useUpdatePurchase();

    const inFlight = useRef(false);
    const lastCommitted = useRef<CreatePurchaseState | null>(null);
    const pendingUpdate = useRef<CreatePurchaseState | null>(null);
    const lastTrigger = useRef(rerunTrigger);

    const { purchaseId } = useCreatePurchaseStore.getState();
    const debouncedPurchase = useDebounce(purchase, { wait: 250, maxWait: 500 });
    const finalPurchase =
        purchaseId && debouncedPurchase.items?.[0]?.id && lastTrigger.current === rerunTrigger
            ? debouncedPurchase
            : purchase;

    useAsyncEffect(async () => {
        const { purchase, isSubmitting, isProcessing } = useCreatePurchaseStore.getState();

        if (purchase.status === PurchaseStatus.COMMITTED) {
            return;
        }

        const hasData = hasEssentialData(finalPurchase);
        const isEligible = (hasData && !showCouponRestriction) || ignoreRestrictions;
        if (!isEligible || (!purchaseId && isProcessing) || isSubmitting) {
            return;
        }

        const changed =
            !lastCommitted.current ||
            hasPurchaseChanged(finalPurchase, lastCommitted.current) ||
            lastTrigger.current !== rerunTrigger;

        if (!changed && !ignoreRestrictions) {
            return;
        }

        if (inFlight.current) {
            pendingUpdate.current = finalPurchase;
            return;
        }

        inFlight.current = true;
        lastCommitted.current = finalPurchase;
        lastTrigger.current = rerunTrigger;

        try {
            const payload = createPurchaseStoreToPayload(finalPurchase);
            const newPurchase = await updatePurchase(payload);

            updatePurchaseData(newPurchase);
        } catch (error) {
            console.error("[usePurchaseManager] API error", error);
            lastCommitted.current = null;
        } finally {
            inFlight.current = false;

            const next = pendingUpdate.current;
            pendingUpdate.current = null;

            if (
                next &&
                hasPurchaseChanged(next, lastCommitted.current) &&
                !useCreatePurchaseStore.getState().isSubmitting
            ) {
                useCreatePurchaseStore.setState({
                    rerunTrigger: rerunTrigger + 1,
                });
            }
        }
    }, [finalPurchase, rerunTrigger, showCouponRestriction, ignoreRestrictions]);
};

const getParams = () => {
    const { shouldIgnoreRestrictions } = useCreatePurchaseStore.getState();
    const { shouldShowCodeRestrictions } = useCreatePurchaseStore.getState();
    const sellerId = getSeller().id;

    const params: { seller: ID; validationLevel?: string } = { seller: sellerId };
    if (shouldIgnoreRestrictions) {
        params.validationLevel = "warning";
    }

    if (shouldShowCodeRestrictions.validationLevel === "info") {
        params.validationLevel = "info";
    }

    return params;
};

export const useUpdatePurchase = () => {
    const mutation = useMutation<Purchase, unknown, CreatePurchaseState>({
        mutationFn: async (payload: CreatePurchaseState) => savePurchase(payload, getParams()),

        onError: (xhr: any) => {
            const validationErrors = xhr.response.data.errors;
            captureAddOnRestrictionErrors(xhr);
            const codeErrorType = getRewardCodeErrorType(validationErrors);

            if (codeErrorType) {
                const errorMessage = lowerCase(validationErrors[0].message);
                const codeErrorMessage =
                    codeErrorType === RewardCodeObject.MEMBERSHIP
                        ? "membership to be removed"
                        : "coupon discount to be zeroed out";

                useCreatePurchaseStore.setState({
                    shouldShowCodeRestrictions: {
                        errorCode: validationErrors[0].code,
                        errorField: validationErrors[0].field,
                        isOpen: true,
                        validationLevel: "",
                        codeType: codeErrorType,
                        message: `In order to use this code, guest ${errorMessage}. Proceeding with this modification will result in the ${codeErrorMessage}. Do you wish to proceed?`,
                    },
                });
            } else {
                useCreatePurchaseStore.setState({ errors: validationErrors });
            }

            /**
             * The case when the seller was afk for long enough for the purchase draft to be deleted.
             */
            if (isPurchaseNotFoundError(xhr)) {
                resetPurchase();
                return;
            }

            /**
             * The case when we have the broken purchase after changing quantity,
             * sometimes with slow connection or fast clicking, add and reduce guest can lead to 'guest_not_found' error,
             * When this happens we need to remove the id from the guest and re-run the trigger which will make the API call again
             */
            if (isGuestNotFoundError(xhr)) {
                const { purchase } = useCreatePurchaseStore.getState();
                const items = purchase.items.map((item) => ({
                    ...item,
                    guests: item.guests
                        ? item.guests.map((guest) => {
                              if (guest.formResponse?.id) {
                                  const guestToReturn = {
                                      ...omit(guest, "id"),
                                      formResponse: omit(guest.formResponse, "id"),
                                  };
                                  return guestToReturn as Guest;
                              }

                              return omit(guest, "id") as Guest;
                          })
                        : [],
                }));

                useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                    draft.items = items;
                });
                useCreatePurchaseStore.setState({
                    rerunTrigger: useCreatePurchaseStore.getState().rerunTrigger + 1,
                });
            }
        },
    });

    return mutation;
};

export const createPurchaseStoreToPayload = (purchase: CreatePurchaseState): any => {
    const { purchaseId } = useCreatePurchaseStore.getState();
    const seller = getSeller();
    const isPackageBooking = isPackagePurchase(purchase);
    const { activeItemIndex } = useCreatePurchaseStore.getState();

    const items = purchase.items.map((item) => {
        const lineItems = excludeCatalogOwnedLineItems(item.lineItems).filter(
            (lineItem: CreatePurchaseItemLineItem) => {
                if (isTaxFee(lineItem) && (lineItem as LineItem).includeInAmount) {
                    return false;
                }

                return true;
            },
        );

        const demographicLineItems = item.demographicLineItems ?? [];
        const equipmentLineItems = item.equipmentLineItems ?? [];
        const attendeeLineItems = (item.attendeeLineItems ?? []).filter((li) => li.quantity > 0);
        const addonLineItems = getAddonLineItemsPayload(item.addonLineItems);
        const wareLineItems = item.wareLineItems ?? [];
        const filteredWareLineItems =
            item.object === PurchaseItemObject.WARE
                ? getFilteredWareLineItems(item.product.id, wareLineItems)
                : wareLineItems;

        const itemLineItems = [
            ...demographicLineItems,
            ...equipmentLineItems,
            ...attendeeLineItems,
            ...addonLineItems,
            ...filteredWareLineItems,
            ...lineItems,
        ];

        const isCustomLineItemsAdded = item.lineItems.some((lineItem) => {
            return lineItem.type && [LineItemType.ADJUSTMENT, LineItemType.TAXED_ADJUSTMENT].includes(lineItem.type);
        });

        return {
            ...omit(
                item,
                "demographicLineItems",
                "equipmentLineItems",
                "attendeeLineItems",
                "addonLineItems",
                "wareLineItems",
                "customLineItems",
            ),
            purchase: { id: purchaseId },
            guests: item.guests ? getGuestsPayload(item).map((guest) => omit(guest, "ticketCode")) : undefined,
            lineItems:
                item.enableCustomLineItems && !isCustomLineItemsAdded
                    ? [...itemLineItems, ...(item.customLineItems ?? [])]
                    : itemLineItems,
        };
    });

    const { customerEmail, customerName, phone } = useCustomerDetailsStore.getState();

    const payload = {
        lineItems: getPurchaseLineItems(purchase),
        items,
        seller: { id: seller.id },
        splitPayment: purchase.splitPayment,
        paymentReminders: purchase.paymentReminders,
        currency: seller.currency,
        status: purchase.status ?? PurchaseStatus.DRAFT,
        id: purchaseId,
        payNow: purchase.payNow ?? null,
        customerEmail,
        customerName,
        phone,
    };

    let sanitizedPayload = payload;

    if (activeItemIndex !== payload.items.length - 1 && !isPackageBooking) {
        sanitizedPayload = {
            ...payload,
            items: items.filter((item) => Boolean(item.id)),
        };
    }

    return sanitizedPayload;
};

const getRewardCodeErrorType = (validationErrors: any): RewardCodeObject | null => {
    const { activeItemIndex } = useCreatePurchaseStore.getState();
    const payload = usePurchaseResponseStore.getState().purchase as Purchase;
    const errorObject = isArray(validationErrors) && validationErrors.length > 0 ? validationErrors[0].object : "";
    if (errorObject !== PurchaseRuleFilterError.VALIDATION_ERROR) {
        return null;
    }

    if (!payload.items[activeItemIndex]?.lineItems) {
        return null;
    }

    const activeItemLineItems = { lineItems: payload.items[activeItemIndex]?.lineItems };

    const hasCouponLineItems =
        getCouponLineItems(activeItemLineItems).length > 0 ||
        getCouponLineItems({ lineItems: payload.lineItems }).length > 0;

    if (hasCouponLineItems) {
        return RewardCodeObject.COUPON;
    }

    const hasMembershipCodeLineItems = getMembershipCodeLineItems(activeItemLineItems).length > 0;
    return hasMembershipCodeLineItems ? RewardCodeObject.MEMBERSHIP : null;
};

const hasEssentialData = (purchase: CreatePurchaseState) => {
    if (useCreatePurchaseStore.getState().isPartnerPackage || isPackagePurchase(purchase)) {
        return true;
    }

    return purchase.items.every((item) => {
        if (isScheduledItem(item)) {
            return !!item?.timeslot;
        }

        return true; // all other types (gift, membership, unscheduled) are valid
    });
};

const hasPurchaseChanged = (current: CreatePurchaseState, previous: CreatePurchaseState | null): boolean => {
    if (!previous) return true;

    if (!current.items || !previous?.items) {
        return true;
    }

    if (current.customerEmail !== previous.customerEmail) return true;
    if (current.items.length !== previous.items.length) return true;
    if (current.lineItems.length !== previous.lineItems.length) return true;
    if (current.payNow !== previous.payNow) return true;

    for (const [index, currentItem] of current.items.entries()) {
        const previousItem = previous.items[index];
        if (previousItem.arrivalDate !== currentItem.arrivalDate) return true;
        if (previousItem.arrivalTime !== currentItem.arrivalTime) return true;
        if (previousItem.privacy !== currentItem.privacy) return true;
        if (previousItem.enableCustomLineItems !== currentItem.enableCustomLineItems) return true;
        if (!isEqual(previousItem.lineItems, currentItem.lineItems)) return true;
        if (!isEqual(previousItem.customLineItems, currentItem.customLineItems)) return true;
        if (!isEqual(previousItem.demographicLineItems, currentItem.demographicLineItems)) return true;
        if (!isEqual(previousItem.equipmentLineItems, currentItem.equipmentLineItems)) return true;
        if (!isEqual(previousItem.attendeeLineItems, currentItem.attendeeLineItems)) return true;
        if (!isEqual(previousItem.addonLineItems, currentItem.addonLineItems)) return true;
        if (!isEqual(previousItem.wareLineItems, currentItem.wareLineItems)) return true;
        if (previousItem.paymentDueOffset !== currentItem.paymentDueOffset) return true;
        if (previousItem.balanceDueOffset !== currentItem.balanceDueOffset) return true;
    }

    return false;
};

export const updatePurchaseData = (newPurchase: Purchase) => {
    if (!isEmpty(useCreatePurchaseStore.getState().errors)) {
        useCreatePurchaseStore.setState({ errors: [] });
    }

    usePurchaseResponseStore.getState().actions.setPurchase(newPurchase);

    useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
        for (const [index, item] of draft.items.entries()) {
            const newPurchaseItem = newPurchase.items[index];
            if (!newPurchaseItem) {
                continue;
            }

            if (item.id !== newPurchaseItem.id) {
                item.id = newPurchaseItem.id;
            }

            if (item.amount !== newPurchaseItem.amount) {
                item.amount = newPurchaseItem.amount;
            }

            if (!areGuestsEqual(item.guests, newPurchaseItem.guests)) {
                item.guests = newPurchaseItem.guests;
            }
        }

        if (newPurchase.status === PurchaseStatus.COMMITTED) {
            draft.status = PurchaseStatus.COMMITTED;
        }
    });

    const { paymentLineItem } = useCreatePurchaseStore.getState();
    const isInvoice = paymentLineItem.payment.method === PaymentMethod.INVOICE;

    if (!isInvoice && (isNil(paymentLineItem.amount) || Math.abs(paymentLineItem.amount ?? 0) !== newPurchase.dueNow)) {
        // Re-initialize the payment line item with the correct amount from the API.
        // This will happen when you send coupons which will change the payment amount
        const method = (paymentLineItem.payment.method ?? PaymentMethod.CREDIT_CARD) as PaymentMethod;

        useCreatePurchaseStore.getState().actions.initializePaymentLineItem(newPurchase.dueNow, method);
    }

    const { isProcessing } = useCreatePurchaseStore.getState();

    useCreatePurchaseStore.setState({
        previousPurchase: newPurchase,
        isProcessing: isProcessing ? false : undefined,
        purchaseId: newPurchase.id,
        balance: newPurchase.balance,
        dueNow: newPurchase.dueNow,
        amount: newPurchase.amount,
        itemUnitSubtotals: newPurchase.items.map((item) => {
            return {
                id: item.id,
                subtotal: item.lineItems.find((lineItem) => lineItem.code === LineItemCode.UNIT)?.amount ?? 0,
            };
        }),
    });
};
